import {
  reorderQuestionFamily,
  surveyOutline,
  type SurveySection,
} from "../../../../shared/survey-questions";
import { AppModal, Button } from "@/components/ui";
import { useState } from "react";
import {
  SurveyQuestionReorderGroup,
  type QuestionSummary,
} from "./SurveyQuestionReorderGroup";

export function ReorderSurveyQuestionsModal<T extends QuestionSummary>({
  questions,
  sections = [],
  onClose,
  onSave,
  onRestoreFocus,
}: {
  questions: T[];
  sections?: SurveySection[];
  onClose: () => void;
  onSave: (questions: T[]) => void;
  onRestoreFocus: () => void;
}) {
  const [draftQuestions, setDraftQuestions] = useState(questions);
  const [scrollElement, setScrollElement] = useState<HTMLDivElement | null>(
    null,
  );
  const outline = surveyOutline(draftQuestions);
  const questionByKey = new Map(
    draftQuestions.map((question) => [question.key, question]),
  );
  const groups: { section: SurveySection | null; questions: T[] }[] =
    sections.length
      ? [...sections]
          .sort((a, b) => a.position - b.position)
          .map((section) => ({
            section,
            questions: draftQuestions.filter(
              (question) =>
                questionByKey.get(outline.get(question.key).root)?.sectionId ===
                section.id,
            ),
          }))
      : [{ section: null, questions: draftQuestions }];

  if (sections.length) {
    const assigned = new Set(groups.flatMap((group) => group.questions));
    const unassigned = draftQuestions.filter(
      (question) => !assigned.has(question),
    );
    if (unassigned.length)
      groups.push({ section: null, questions: unassigned });
  }

  return (
    <AppModal
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      onCloseAutoFocus={(event) => {
        event.preventDefault();
        onRestoreFocus();
      }}
      title="Reorder questions"
      description={
        sections.length
          ? "Reorder questions within each section. Follow-ups stay with their parent."
          : "Reorder questions. Follow-ups stay with their parent."
      }
      size="lg"
      bodyContained
      overlayClassName="z-[108]"
      className="z-[109]"
      footer={
        <div className="flex w-full justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => {
              onSave(draftQuestions);
              onClose();
            }}
          >
            Save order
          </Button>
        </div>
      }
    >
      <div
        ref={setScrollElement}
        className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-5"
      >
        <p className="text-muted-foreground text-sm">
          {sections.length
            ? "Drag questions within a section. To move a question to another section, change its Section dropdown in the survey editor."
            : "Drag questions to reorder them."}{" "}
          Follow-ups move with their parent and can be reordered among siblings.
        </p>
        {groups.map(({ section, questions: groupQuestions }) => (
          <section
            key={section?.id ?? "questions"}
            aria-label={
              section
                ? section.title || "Untitled section"
                : sections.length
                  ? "Unassigned questions"
                  : "Questions"
            }
            className="border-border overflow-hidden rounded-xl border"
          >
            {section || sections.length ? (
              <h3 className="bg-muted/50 border-b px-4 py-3 text-sm font-medium">
                {section
                  ? section.title || "Untitled section"
                  : "Unassigned questions"}
              </h3>
            ) : null}
            {groupQuestions.length ? (
              <SurveyQuestionReorderGroup
                questions={groupQuestions}
                outline={outline}
                startIndex={draftQuestions.indexOf(groupQuestions[0])}
                scrollElement={scrollElement}
                onReorder={(from, to) => {
                  setDraftQuestions((current) =>
                    reorderQuestionFamily(current, from, to),
                  );
                }}
              />
            ) : (
              <p className="text-muted-foreground px-4 py-3 text-sm">
                No questions in this section.
              </p>
            )}
          </section>
        ))}
      </div>
    </AppModal>
  );
}
